import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { dayMonthYear } from "../../values/dateFormatter";
import { colors } from "../../values/colors";
import {
  useDeclareMutation,
  usePickNextPlayerMutation,
} from "../../services/auctionAPI";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// 👇 Import your SVG icons
import { ReactComponent as UserIcon } from "../../assets/icons/user.svg";
import { ReactComponent as PhoneIcon } from "../../assets/icons/phone.svg";
import { ReactComponent as MailIcon } from "../../assets/icons/mail.svg";

import defaultTournamentLogo from "../../assets/images/defaultTournamentLogo.png";
import { Place } from "@mui/icons-material";

const AuctionInfo = ({ data }) => {
  const [declare, { isLoading }] = useDeclareMutation();
  const [pickNextPlayer, { data: player, isLoading: pickPlayerLoading }] =
    usePickNextPlayerMutation();
  const navigate = useNavigate();

  const handleAuction = async () => {
    const payload = {
      isStarted: true,
      isPaused: false,
      isEnd: false,
      auctionId: data?.auctionDetails?._id,
    };
    try {
      sessionStorage.clear();
      const res = await declare(payload);
      if (res?.data?.success) {
        navigate("/auction/bidding");
      }
    } catch (error) {
      toast.error(error?.data?.message || "Something went wrong");
    }
  };
  const isEnd = data?.auctionDetails?.isEnd;

  console.log(data, "data2465");

  return (
    <Box
      sx={{
        border: "1px solid #E0E0E0",
        borderRadius: "1rem",
        p: "2rem",
        maxWidth: "75%",
        margin: "2rem auto 4rem auto",
        backgroundColor: "#fff",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
      }}
    >
      {/* Header Section: Logo + Tournament Name + Location */}
      <Box sx={{ display: "flex", gap: "1.5rem", mb: "2rem", alignItems: "center" }}>
        <Box
          component="img"
          src={data?.logo || defaultTournamentLogo}
          alt="Tournament Logo"
          sx={{
            width: "120px",
            height: "120px",
            objectFit: "contain",
            // border: "1px solid #E0E0E0",
          }}
        />
        <Box>
          <Typography
            sx={{
              fontSize: "32px",
              fontWeight: "600",
              color: "#333",
              lineHeight: 1.2,
              mb: "0.5rem",
            }}
          >
            {data?.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Place sx={{ color: "#666", fontSize: "18px" }} />
            <Typography sx={{ fontSize: "18px", color: "#666" }}>
              {data?.groundId?.name}, {data?.city?.name}
              {/* {data?.city?.stateName} */}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Details Section */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "2rem",
          mb: "2rem",
        }}
      >
        {/* Auction Date */}
        <Box sx={{ minWidth: "250px" }}>
          <Typography
            sx={{ fontSize: "24px", fontWeight: "600", mb: "0.5rem", textTransform: "uppercase" }}
          >
            AUCTION DATE
          </Typography>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 700,
              color: colors.accent, // Orange color
            }}
          >
            {dayMonthYear(data?.auctionDetails?.date)}
          </Typography>
        </Box>

        {/* Auctioneer Details */}
        <Box sx={{ minWidth: "300px", flex: 1 }}>
          <Typography
            sx={{ fontSize: "24px", fontWeight: "600", mb: "1rem", textTransform: "uppercase" }}
          >
            AUCTIONEER DETAILS
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1.5rem", mb: "0.5rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <UserIcon style={{ width: 18, height: 18, fill: colors.primary }} />
              <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#333" }}>
                {data?.auctionDetails?.auctioner?.name}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <PhoneIcon style={{ width: 18, height: 18, fill: colors.primary }} />
              <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#333" }}>
                +{data?.auctionDetails?.auctioner?.phoneArea} {data?.auctionDetails?.auctioner?.phone}
              </Typography>
            </Box>
          </Box>

          {data?.auctionDetails?.auctioner?.email && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <MailIcon style={{ width: 18, height: 18, fill: colors.primary }} />
              <Typography sx={{ fontSize: "18px", fontWeight: "500", color: "#333" }}>
                {data?.auctionDetails?.auctioner?.email}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Auction Summary */}
        <Box sx={{ minWidth: "300px", flex: 1 }}>
          <Typography
            sx={{ fontSize: "24px", fontWeight: "600", mb: "1rem", textTransform: "uppercase" }}
          >
            AUCTION SUMMARY
          </Typography>
          <Box sx={{ display: "flex", gap: "3rem" }}>
            <Box>
              <Typography sx={{ fontSize: "22px", fontWeight: "600", textTransform: "uppercase", mb: "0.2rem" }}>
                POINTS PER TEAM
              </Typography>
              <Typography sx={{ fontSize: "22px", fontWeight: "700", color: colors.accent }}>
                {data?.auctionDetails?.pointsPerTeam?.toLocaleString()}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "22px", color: "#666", fontWeight: "600", textTransform: "uppercase", mb: "0.2rem" }}>
                TEAMS
              </Typography>
              <Typography sx={{ fontSize: "22px", fontWeight: "700", color: colors.accent }}>
                {data?.teamCount}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: "22px", color: "#666", fontWeight: "600", textTransform: "uppercase", mb: "0.2rem" }}>
                PLAYERS
              </Typography>
              <Typography sx={{ fontSize: "22px", fontWeight: "700", color: colors.accent }}>
                {data?.teamMembers?.length}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ mb: "1.5rem", borderColor: "#E0E0E0" }} />

      {/* Button */}
      {!isEnd && (
        <Button
          variant="contained"
          sx={{
            background: colors.linearGradent || "linear-gradient(90deg, #F27121 0%, #E94057 100%)", // Fallback if linearGradent not defined, but using usage from file
            borderRadius: "3rem",
            width: "100%",
            py: "0.8rem",
            textTransform: "capitalize",
            fontSize: "18px",
            fontWeight: "600",
            boxShadow: "none",
            "&:hover": { boxShadow: "0px 4px 15px rgba(233, 64, 87, 0.4)" }
          }}
          onClick={handleAuction}
          disabled={isLoading || pickPlayerLoading}
        >
          {isLoading || pickPlayerLoading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : data?.auctionDetails?.isStarted ? (
            "Resume Bidding"
          ) : (
            "Start Bidding"
          )}
        </Button>
      )}
    </Box>
  );
};

export default AuctionInfo;
